import React from 'react';
import styled, { keyframes } from 'styled-components';
import { blueGrey } from '../../design-system/theme';

// ===================================
// Stat Card Component
// ===================================

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const StatCardContainer = styled.div<{ $color?: string; $delay?: number }>`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border-left: 4px solid ${props => props.$color || 'var(--ds-primary, #363a50)'};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  animation: ${fadeIn} 0.4s ease forwards;
  animation-delay: ${props => (props.$delay || 0) * 0.1}s;
  opacity: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

const StatCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const StatCardIcon = styled.div<{ $color?: string }>`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: ${props => props.$color ? `${props.$color}15` : 'var(--ds-surface-muted)'};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 22px;
    height: 22px;
    color: ${props => props.$color || 'var(--ds-primary)'};
  }
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: var(--ds-text-secondary, #565a74);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: var(--ds-text-primary, #212435);
  line-height: 1.2;
`;

const StatTrend = styled.div<{ $positive?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.$positive !== false ? 'var(--ds-success, #11936f)' : 'var(--ds-danger, #e51720)'};
  margin-top: 8px;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const StatSubtext = styled.div`
  font-size: 13px;
  color: var(--ds-text-subtle, #8b8fa9);
  margin-top: 8px;
`;

interface StatCardProps {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  color?: string;
  trend?: {
    value: string;
    positive?: boolean;
  };
  subtext?: string;
  delay?: number;
}

export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon,
  color,
  trend,
  subtext,
  delay = 0,
}) => {
  return (
    <StatCardContainer $color={color} $delay={delay}>
      <StatCardHeader>
        <StatLabel>{label}</StatLabel>
        {icon && <StatCardIcon $color={color}>{icon}</StatCardIcon>}
      </StatCardHeader>
      <StatValue>{typeof value === 'number' ? value.toLocaleString() : value}</StatValue>
      {trend && (
        <StatTrend $positive={trend.positive}>
          {trend.positive !== false ? (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          ) : (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
            </svg>
          )}
          {trend.value}
        </StatTrend>
      )}
      {subtext && <StatSubtext>{subtext}</StatSubtext>}
    </StatCardContainer>
  );
};

// ===================================
// Welcome Card Component
// ===================================

const WelcomeContainer = styled.div`
  background: ${blueGrey[10]};
  border-radius: 16px;
  padding: 32px;
  color: white;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.4s ease forwards;
`;

const WelcomeContent = styled.div`
  position: relative;
  z-index: 1;
`;

const WelcomeTitle = styled.h1`
  font-size: 28px;
  margin: 0 0 8px 0;
  font-weight: 600;
`;

const WelcomeSubtitle = styled.p`
  font-size: 15px;
  margin: 0;
  opacity: 0.9;
  max-width: 600px;
`;

const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-top: 24px;
`;

const QuickActionButton = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  color: white;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: background 0.2s ease, transform 0.2s ease;
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

interface QuickAction {
  label: string;
  href: string;
  icon: React.ReactNode;
}

interface WelcomeCardProps {
  userName?: string;
  subtitle?: string;
  quickActions?: QuickAction[];
}

export const WelcomeCard: React.FC<WelcomeCardProps> = ({
  userName,
  subtitle = 'Manage your consent templates, fiduciaries, and monitor consent artifacts.',
  quickActions,
}) => {
  return (
    <WelcomeContainer>
      <WelcomeContent>
        <WelcomeTitle>
          Welcome back{userName ? `, ${userName}` : ''}! 👋
        </WelcomeTitle>
        <WelcomeSubtitle>{subtitle}</WelcomeSubtitle>

        {quickActions && quickActions.length > 0 && (
          <QuickActionsGrid>
            {quickActions.map((action, index) => (
              <QuickActionButton key={index} href={action.href}>
                {action.icon}
                {action.label}
              </QuickActionButton>
            ))}
          </QuickActionsGrid>
        )}
      </WelcomeContent>
    </WelcomeContainer>
  );
};

// ===================================
// Section Card Component
// ===================================

const SectionContainer = styled.div<{ $padding?: string }>`
  background: white;
  border-radius: 12px;
  padding: ${props => props.$padding || '24px'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const SectionTitleStyled = styled.h2`
  font-size: 18px;
  color: var(--ds-text-primary, #212435);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SectionAction = styled.a`
  font-size: 14px;
  color: var(--ds-primary, #363a50);
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

interface SectionCardProps {
  title: string;
  icon?: React.ReactNode;
  action?: { label: string; href: string };
  padding?: string;
  children: React.ReactNode;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  title,
  icon,
  action,
  padding,
  children,
}) => {
  return (
    <SectionContainer $padding={padding}>
      <SectionHeader>
        <SectionTitleStyled>
          {icon}
          {title}
        </SectionTitleStyled>
        {action && <SectionAction href={action.href}>{action.label} →</SectionAction>}
      </SectionHeader>
      {children}
    </SectionContainer>
  );
};

// ===================================
// Activity Item Component
// ===================================

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ActivityItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--ds-surface-muted, #f7f7fb);
  border-radius: 8px;
  transition: background 0.15s ease;

  &:hover {
    background: var(--ds-surface-strong, #e5e7ef);
  }
`;

const ActivityInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ActivityTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--ds-text-primary, #212435);
`;

const ActivityTime = styled.div`
  font-size: 12px;
  color: var(--ds-text-subtle, #8b8fa9);
`;

const ActivityBadge = styled.span<{ $variant?: 'success' | 'warning' | 'info' | 'danger' }>`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch (props.$variant) {
      case 'success': return '#dcfce7';
      case 'warning': return '#fef3c7';
      case 'danger': return '#fee2e2';
      default: return '#dbeafe';
    }
  }};
  color: ${props => {
    switch (props.$variant) {
      case 'success': return '#166534';
      case 'warning': return '#92400e';
      case 'danger': return '#991b1b';
      default: return '#1e40af';
    }
  }};
`;

interface ActivityItemProps {
  title: string;
  time: string;
  badge?: {
    label: string;
    variant?: 'success' | 'warning' | 'info' | 'danger';
  };
}

export const ActivityItem: React.FC<ActivityItemProps> = ({ title, time, badge }) => {
  return (
    <ActivityItemContainer>
      <ActivityInfo>
        <ActivityTitle>{title}</ActivityTitle>
        <ActivityTime>{time}</ActivityTime>
      </ActivityInfo>
      {badge && <ActivityBadge $variant={badge.variant}>{badge.label}</ActivityBadge>}
    </ActivityItemContainer>
  );
};

export { ActivityList };

// ===================================
// Empty State Component
// ===================================

const EmptyStateContainer = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyStateTitle = styled.h3`
  font-size: 18px;
  color: var(--ds-text-primary, #212435);
  margin: 0 0 8px 0;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: var(--ds-text-secondary, #565a74);
  margin: 0;
  max-width: 400px;
  margin: 0 auto;
`;

interface EmptyStateProps {
  icon?: string;
  title: string;
  text?: string;
  action?: React.ReactNode;
}

export const EmptyState: React.FC<EmptyStateProps> = ({ icon = '📭', title, text, action }) => {
  return (
    <EmptyStateContainer>
      <EmptyStateIcon>{icon}</EmptyStateIcon>
      <EmptyStateTitle>{title}</EmptyStateTitle>
      {text && <EmptyStateText>{text}</EmptyStateText>}
      {action && <div style={{ marginTop: 20 }}>{action}</div>}
    </EmptyStateContainer>
  );
};

// ===================================
// Stats Grid Component
// ===================================

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
`;

// ===================================
// Two Column Grid
// ===================================

export const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

// ===================================
// Loading Skeleton
// ===================================

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

export const Skeleton = styled.div<{ $width?: string; $height?: string }>`
  width: ${props => props.$width || '100%'};
  height: ${props => props.$height || '20px'};
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
`;
